import posthog from "posthog-js";

/** @type {boolean} */
let initialized = false;

const DEFAULT_API_HOST = "https://us.i.posthog.com";

function getApiKey() {
    return typeof process !== "undefined" ? process.env.NEXT_PUBLIC_POSTHOG_KEY : "";
}

function getApiHost() {
    const host = typeof process !== "undefined" ? process.env.NEXT_PUBLIC_POSTHOG_HOST : "";
    return host && host.trim() ? host.trim().replace(/\/+$/, "") : DEFAULT_API_HOST;
}

/**
 * Initialize PostHog once in the browser when NEXT_PUBLIC_POSTHOG_KEY is set.
 */
export function initPostHogClient() {
    if (typeof window === "undefined" || initialized) return false;
    // Invitation setup links carry their token in the path; never let it reach
    // PostHog's current/initial URL properties (SCHED-699).
    if (window.location.pathname.startsWith("/invitation/")) return false;
    const key = getApiKey();
    if (!key) return false;

    try {
        posthog.init(key, {
            api_host: getApiHost(),
            // Only send the explicit events emitted via utilities/analytics.js.
            // Disable PostHog's automatic client-side captures so Activity stays clean.
            autocapture: false,
            capture_pageview: false,
            capture_pageleave: false,
            capture_heatmaps: false,
            // Explicit object overrides PostHog project-side web vitals settings (remote config).
            capture_performance: {
                web_vitals: false,
                network_timing: false,
            },
            disable_session_recording: true,
            // Drop any automatic web vitals that still slip through (e.g. remote config race).
            before_send: (event) => (event?.event === "$web_vitals" ? null : event),
            persistence: "localStorage+cookie",
            person_profiles: "identified_only",
        });
        initialized = true;
        return true;
    } catch {
        return false;
    }
}

export function isPostHogInitialized() {
    return initialized && Boolean(getApiKey());
}

/**
 * Persist super-properties replicated on captures (analytics layer also merges explicitly).
 */
export function registerPostHogSuperProps(props) {
    if (!isPostHogInitialized() || !props || typeof props !== "object") return;
    try {
        posthog.register(props);
    } catch {
        /* fail-open */
    }
}

/**
 * @param {string} eventName
 * @param {Record<string, unknown>} properties
 * @param {{ sendInstantly?: boolean }} [options] When `sendInstantly`, asks the SDK to flush right away
 *   (used for events emitted just before a navigation, e.g. SSO start, external links).
 */
export function capturePostHogEvent(eventName, properties, options) {
    if (!isPostHogInitialized() || !eventName) return;
    try {
        const props = properties && typeof properties === "object" ? properties : {};
        if (options && options.sendInstantly) {
            posthog.capture(eventName, props, { send_instantly: true });
        } else {
            posthog.capture(eventName, props);
        }
    } catch {
        /* fail-open */
    }
}

/**
 * Distinct ID must already be hashed / non-PII per product policy.
 * @param {string} distinctId
 * @param {Record<string, unknown>} [personProperties] Person properties (optional), e.g. `email`, `name`, `org_id`, `role`.
 */
export function identifyPostHogUser(distinctId, personProperties) {
    if (!isPostHogInitialized() || !distinctId) return;
    try {
        posthog.identify(String(distinctId), personProperties && typeof personProperties === "object" ? personProperties : {});
    } catch {
        /* fail-open */
    }
}

/**
 * Call on logout or session teardown so the next visitor on a shared browser is not merged.
 */
export function resetPostHogIdentity() {
    if (!initialized) return;
    try {
        posthog.reset();
    } catch {
        // Ignore if SDK is in a bad state
    }
}
